import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon, EllipsisVerticalIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react'
import {
	Avatar, AvatarFallback, AvatarImage,
	Badge,
	Button,
	ConfirmDialog,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { useDeleteContactMutation, useListContactsQuery, useListDealsQuery, useListPropertiesQuery } from '@/api'
import { formatPropertyAddress } from '@/lib/property-address'
import { ChangeReliabilityDialog } from '@/components/change-reliability-dialog'
import { PanelDown } from '@/components/panel-down'
import type { Contact, ContactReliability, Deal, Property } from '@/types'
import { DEFAULT_LOCALE } from '@/types'
import { getInitials } from '@/lib/text-utils'

const PAGE_SIZE = 10

type TenantPeriod = 'current' | 'upcoming' | 'past'
type ReliabilityStatus = 'positive' | 'moderate' | 'negative'
type SortField = 'name' | 'nextPayment'
type SortDir = 'asc' | 'desc'

const RELIABILITY_VARIANTS: Record<ReliabilityStatus, 'success' | 'warning' | 'destructive'> = {
	positive: 'success',
	moderate: 'warning',
	negative: 'destructive',
}

interface TenantRow {
	contact: Contact
	deal?: Deal
	property?: Property
	reliability?: ReliabilityStatus
}

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function buildPropertyLabel(p: Property, t: (key: string, opts?: Record<string, unknown>) => string): string {
	const typeLabel = p.rooms
		? `${t('properties.roomsSuffix', { rooms: p.rooms })} ${t(`properties.types.${p.type}`).toLowerCase()}`
		: t(`properties.types.${p.type}`)
	return `${typeLabel} ${formatPropertyAddress(p.city, p.address)}`
}

function filterByPeriod(deal: Deal | undefined, property: Property | undefined, period: TenantPeriod): boolean {
	if (!deal || !property) return false

	const now = Date.now()
	const start = deal.startDate ? new Date(deal.startDate).getTime() : 0
	const end = deal.endDate ? new Date(deal.endDate).getTime() : Infinity

	if (deal.startDate && deal.endDate) {
		if (period === 'current') return start <= now && now <= end
		if (period === 'upcoming') return start > now
		if (period === 'past') return end < now
	}

	// Fallback to deal status when dates are missing
	if (period === 'current') return deal.status === 'COMPLETED'
	if (period === 'upcoming') return deal.status === 'ACTIVE'
	if (period === 'past') return deal.status === 'CANCELLED'
	return false
}

interface TenantListPageProps {
	period: TenantPeriod
	emptyTitle: string
	emptyDescription: string
}

export function TenantListPage({ period, emptyTitle, emptyDescription }: TenantListPageProps) {
	const { t } = useTranslation()
	const [statusDialog, setStatusDialog] = useState<{ id: string; current?: ContactReliability } | null>(null)
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [sortField, setSortField] = useState<SortField>('name')
	const [sortDir, setSortDir] = useState<SortDir>('asc')
	const [propertyFilter, setPropertyFilter] = useState('')
	const [periodFilter, setPeriodFilter] = useState('')

	const [deleteContact] = useDeleteContactMutation()
	const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

	const { data: contactsData } = useListContactsQuery({ type: 'TENANT' })
	const { data: dealsData } = useListDealsQuery()
	const { data: propertiesData } = useListPropertiesQuery({ limit: 1000 })

	const propertiesMap = useMemo(() => {
		const map = new Map<string, Property>()
		for (const p of propertiesData?.properties ?? []) map.set(p.id, p)
		return map
	}, [propertiesData])

	const rows = useMemo(() => {
		const contacts = contactsData?.items ?? []
		const deals = dealsData?.items ?? []

		const dealsByContact = new Map<string, Deal>()
		for (const d of deals) {
			if (!d.contactId) continue
			const existing = dealsByContact.get(d.contactId)
			if (!existing || (d.status === 'COMPLETED' && existing.status !== 'COMPLETED')) {
				dealsByContact.set(d.contactId, d)
			}
		}

		return contacts
			.filter((c: Contact) => c.type === 'TENANT')
			.map((contact: Contact): TenantRow => {
				const deal = dealsByContact.get(contact.id)
				const property = deal?.propertyId ? propertiesMap.get(deal.propertyId) : undefined
				const reliability = contact.status?.toLowerCase() as ReliabilityStatus | undefined
				return { contact, deal, property, reliability }
			})
			.filter((row: TenantRow) => filterByPeriod(row.deal, row.property, period))
	}, [contactsData, dealsData, propertiesMap, period])

	const periodOptions = useMemo(() => {
		const years = new Set<string>()
		for (const row of rows) {
			if (row.deal?.startDate) years.add(new Date(row.deal.startDate).getFullYear().toString())
			if (row.deal?.endDate) years.add(new Date(row.deal.endDate).getFullYear().toString())
		}
		return [...years].sort().reverse()
	}, [rows])

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		return rows
			.filter((row: TenantRow) => !propertyFilter || row.deal?.propertyId === propertyFilter)
			.filter((row: TenantRow) => {
				if (!periodFilter) return true
				const year = Number(periodFilter)
				const start = row.deal?.startDate ? new Date(row.deal.startDate).getFullYear() : null
				const end = row.deal?.endDate ? new Date(row.deal.endDate).getFullYear() : null
				return start === year || end === year || (start && end && start <= year && year <= end)
			})
			.filter((row: TenantRow) => {
				if (!q) return true
				const { contact, property } = row
				return contact.name.toLowerCase().includes(q)
					|| contact.phone?.toLowerCase().includes(q)
					|| contact.email?.toLowerCase().includes(q)
					|| (property && buildPropertyLabel(property, t).toLowerCase().includes(q))
			})
	}, [rows, search, propertyFilter, periodFilter, t])

	const sorted = useMemo(() => {
		const arr = [...filtered]
		const dir = sortDir === 'asc' ? 1 : -1
		arr.sort((a, b) => {
			if (sortField === 'name') return a.contact.name.localeCompare(b.contact.name) * dir
			if (sortField === 'nextPayment') {
				const da = a.deal?.endDate ?? a.deal?.createdAt ?? ''
				const db = b.deal?.endDate ?? b.deal?.createdAt ?? ''
				return da.localeCompare(db) * dir
			}
			return 0
		})
		return arr
	}, [filtered, sortField, sortDir])

	const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

	const propertyOptions = useMemo(() => {
		const map = new Map<string, string>()
		for (const row of rows) {
			if (row.deal?.propertyId && row.property) {
				map.set(row.deal.propertyId, buildPropertyLabel(row.property, t))
			}
		}
		return [...map.entries()]
	}, [rows, t])

	function toggleSort(field: SortField) {
		if (sortField === field) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortField(field)
			setSortDir('asc')
		}
	}

	const isPast = period === 'past'

	return (
		<div className="flex h-full flex-col">
			<PanelFilters
				filters={
					<>
						<Select
							value={propertyFilter || undefined}
							onValueChange={(v) => { setPropertyFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('tenants.filters.property')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('tenants.filters.allProperties')}</SelectItem>
								{propertyOptions.map(([id, label]) => (
									<SelectItem key={id} value={id}>{label}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={periodFilter || undefined}
							onValueChange={(v) => { setPeriodFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('tenants.filters.rentalPeriod')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('tenants.filters.allPeriods')}</SelectItem>
								{periodOptions.map((year) => (
									<SelectItem key={year} value={year}>{year}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				}
				actions={
					<SearchInput
						placeholder={t('tenants.search')}
						value={search}
						className="w-[200px]"
						onChange={(e) => { setSearch(e.target.value); setPage(1) }}
					/>
				}
			/>

			{sorted.length === 0 ? (
				<div className="flex flex-1 flex-col items-center justify-center gap-2 px-4">
					<p className="text-base font-[500] leading-6 text-(--color-foreground)">
						{emptyTitle}
					</p>
					<p className="text-sm text-(--color-muted-foreground)">
						{emptyDescription}
					</p>
				</div>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table>
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead>
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('name')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('tenants.table.tenant')}
											</Button>
										</TableHead>
										<TableHead className="font-[500] text-(--color-muted-foreground)">
											{t('tenants.table.rental')}
										</TableHead>
										{isPast ? (
										<>
											<TableHead className="w-[200px] font-[500] text-(--color-muted-foreground)">
												{t('tenants.table.reliability')}
											</TableHead>
											<TableHead className="w-[48px]" />
										</>
									) : (
										<TableHead className="w-[184px]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('nextPayment')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('tenants.table.nextPayment')}
											</Button>
										</TableHead>
									)}
									</TableRow>
								</TableHeader>
								<TableBody>
									{paged.map(({ contact, deal, property, reliability }: TenantRow) => (
										<TableRow
										key={contact.id}
										className="h-24 cursor-pointer"
										onClick={() => navigate({ to: '/tenants/$tenantId', params: { tenantId: contact.id } })}
									>
											<TableCell className="px-4">
												<div className="flex items-center gap-3">
													<Avatar className="size-6">
														<AvatarImage src={undefined} />
														<AvatarFallback className="text-xs">
															{getInitials(contact.name)}
														</AvatarFallback>
													</Avatar>
													<span className="text-sm text-(--color-foreground)">
														{contact.name}
													</span>
													{contact.mobileAccessEnabled && (
														<Badge variant="secondary" size="sm">
															{t('tenants.mobileAccess.badge')}
														</Badge>
													)}
												</div>
											</TableCell>
											<TableCell className="px-4">
												<div className="flex flex-col gap-1">
													<span className="text-sm font-[500] leading-none text-(--color-info)">
														{property ? buildPropertyLabel(property, t) : '—'}
													</span>
													<span className="text-sm leading-5 text-(--color-muted-foreground)">
														{deal?.startDate && deal?.endDate
															? `${formatDate(deal.startDate)} - ${formatDate(deal.endDate)}`
															: '—'}
													</span>
												</div>
											</TableCell>
											{isPast ? (
												<>
													<TableCell className="w-[200px] px-4">
														{reliability && (
															<Badge variant={RELIABILITY_VARIANTS[reliability]} size="sm">
																{t(`tenants.reliability.${reliability}`)}
															</Badge>
														)}
													</TableCell>
													<TableCell className="w-[48px] px-4">
														<DropdownMenu>
															<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label="actions" />}>
																<EllipsisVerticalIcon className="size-4" />
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end" className="min-w-40">
																<DropdownMenuItem
																	onClick={() => setStatusDialog({ id: contact.id, current: contact.status })}
																>
																	<RefreshCwIcon className="mr-2 size-4" />
																	{t('tenants.actions.changeStatus')}
																</DropdownMenuItem>
																<DropdownMenuItem
																	className="text-(--color-destructive)"
																	onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(contact.id) }}
																>
																	<Trash2Icon className="mr-2 size-4" />
																	{t('tenants.actions.delete')}
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													</TableCell>
												</>
											) : (
												<TableCell className="w-[184px] px-4 text-sm text-(--color-foreground)">
													{deal?.endDate ? formatDate(deal.endDate) : '—'}
												</TableCell>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={sorted.length} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}

			{statusDialog && (
				<ChangeReliabilityDialog
					open={true}
					onOpenChange={(open) => { if (!open) setStatusDialog(null) }}
					tenantId={statusDialog.id}
					currentStatus={statusDialog.current}
				/>
			)}

			<ConfirmDialog
				open={deleteConfirmId !== null}
				onOpenChange={(open) => { if (!open) setDeleteConfirmId(null) }}
				title={t('tenants.deleteDialog.title')}
				description={t('tenants.deleteDialog.description')}
				confirmLabel={t('tenants.deleteDialog.confirm')}
				cancelLabel={t('common.cancel')}
				variant="destructive"
				onConfirm={async () => {
					if (deleteConfirmId) await deleteContact(deleteConfirmId)
					setDeleteConfirmId(null)
				}}
			/>
		</div>
	)
}
